import { normalizeForSearch } from './textSearch';

/** A half-open `[start, end)` slice of the ORIGINAL text to wrap in `<mark>`. */
export interface HighlightRange {
  start: number;
  end: number;
}

/**
 * Fold one character exactly the way `normalizeForSearch` folds a whole
 * string. Safe to apply per character for this alphabet: NFD decomposes each
 * Vietnamese precomposed letter independently of its neighbours, and the
 * `đ`→`d` fold is a single-character substitution — so folding character by
 * character and concatenating yields the same string as folding the whole
 * thing, which is exactly what lets us keep an index map alongside it.
 *
 * Can return '' (a lone combining mark in already-decomposed text) or more
 * than one character (a letter whose lowercase form expands). Both are handled
 * by the map below.
 */
function foldChar(ch: string): string {
  return normalizeForSearch(ch);
}

/**
 * The folded text plus, for every character of it, the index of the ORIGINAL
 * character it came from.
 *
 * This map is the whole point of the module. Folding changes length in both
 * directions — "Hà" (2) folds to "ha" (2), but an already-decomposed
 * "Hà" (3) folds to "ha" (2) — so matching on the folded string and then
 * slicing the original with folded indices cuts Vietnamese names apart. The
 * matcher (`matchesInvoiceSearch`) never had to care because it only answers
 * yes/no; a highlight has to point at the original characters.
 */
function foldWithMap(text: string): { folded: string; map: number[] } {
  const chars: string[] = [];
  const map: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const folded = foldChar(text[i]!);
    for (const f of folded) {
      chars.push(f);
      map.push(i);
    }
  }
  return { folded: chars.join(''), map };
}

/**
 * Every non-overlapping place `query` matches inside `text`, as ranges over the
 * ORIGINAL string — accent-insensitively, using the same folding the invoices
 * search box matches with (`lib/invoiceSearch.ts` → `normalizeForSearch`), so
 * a row that matched can always show WHY it matched (design §3).
 *
 * Empty/whitespace query, empty text, or no match → `[]`, and the caller
 * renders plain text.
 */
export function highlightRanges(text: string, query: string): HighlightRange[] {
  const needle = normalizeForSearch(query.trim());
  if (needle === '' || text === '') return [];

  const { folded, map } = foldWithMap(text);
  if (folded === '') return [];

  const ranges: HighlightRange[] = [];
  let from = 0;
  for (;;) {
    const hit = folded.indexOf(needle, from);
    if (hit === -1) break;
    const start = map[hit]!;
    // The last MATCHED folded character's original index, +1 — not `map[hit +
    // needle.length]`, which would be the original index of the character
    // AFTER the match and can equal `start` when one original character folded
    // to several, producing an empty range.
    let end = map[hit + needle.length - 1]! + 1;
    // Swallow any following original characters that fold to nothing: a
    // combining accent in already-decomposed source text belongs inside the
    // <mark> with the letter it sits on, not stranded just past it.
    while (end < text.length && foldChar(text[end]!) === '') end += 1;
    ranges.push({ start, end });
    from = hit + needle.length;
  }
  return ranges;
}
